import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { NavigationMenuItem, NavigationCTAButtons, BrandingSettings, ContactSettings, ContentLabels, StickyActionTexts, SectionSocialProof } from '../../types/navigation';

interface NavigationSettingsProps {
  projectId: Id<"projects">;
  languageCode: string;
}

export default function NavigationSettings({ projectId, languageCode }: NavigationSettingsProps) {
  const [menuItems, setMenuItems] = useState<NavigationMenuItem[]>([]);
  const [ctaButtons, setCtaButtons] = useState<NavigationCTAButtons>({ primary: '', secondary: '' });
  const [brandingSettings, setBrandingSettings] = useState<BrandingSettings>({ 
    logoText: '', 
    showLogo: true 
  });
  const [contactSettings, setContactSettings] = useState<ContactSettings>({
    whatsappNumber: '',
    whatsappMessage: '',
    displayPhoneNumber: true
  });
  const [contentLabels, setContentLabels] = useState<ContentLabels>({
    availableUnitTypes: '',
    unitSize: '',
    startingFrom: '',
    downloadBrochure: '',
    contactExpert: '',
    viewDetails: '',
    requestInfo: '',
    bookTour: '',
    whatsappAction: '',
    callAction: '',
    learnMore: '',
    viewAll: '',
    showMore: '',
    showLess: '',
    // Payment Plan Labels
    paymentPlan: '',
    downPayment: '',
    dueBy: '',
    noPaymentPlans: '',
    // FAQ Labels
    frequentlyAskedQuestions: '',
    faqDescription: '',
    // Location Labels
    primeLocation: '',
    nearbyPlaces: '',
    keyDistances: '',
    noNearbyPlaces: '',
    noKeyDistances: '',
    // Gallery Labels
    projectGallery: '',
    closeGallery: '',
    // Legal Info Labels
    dldPermitNumber: '',
    scanQrCode: '',
    // Form Labels
    fullName: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    continue: '',
    back: '',
    submit: '',
    submitting: '',
    thankYou: '',
    // Validation Messages
    nameRequired: '',
    emailRequired: '',
    emailInvalid: '',
    phoneRequired: '',
    searchCountries: '',
    
    // Lead Form Content
    getPersonalizedDetails: '',
    speakWithExperts: '',
    getStartedNow: '',
    takesOnlySeconds: '',
    
    // Step Titles
    letsGetToKnowYou: '',
    whatBringsYouHere: '',
    investmentPreferences: '',
    yourDreamHome: '',
    howToContactYou: '',
    
    // Step Descriptions
    enterDetailsForPersonalized: '',
    selectPrimaryInterest: '',
    tellUsInvestmentPreferences: '',
    tellUsIdealHome: '',
    choosePreferredContact: '',
    
    // Purpose Options
    investment: '',
    endUser: '',
    lookingForInvestment: '',
    searchingForHome: '',
    
    // Investment Labels
    investmentBudget: '',
    investmentTimeline: '',
    
    // End User Labels
    preferredSize: '',
    budgetRange: '',
    
    // Contact Options
    whatsappContact: '',
    phoneCall: '',
    emailOption: '',
    getInstantUpdates: '',
    speakDirectlyExperts: '',
    receiveDetailedEmail: '',
    
    // Success Messages
    willContactYou: '',
    
    // Lead Form Benefits
    benefit1: '',
    benefit2: '',
    benefit3: '',
    benefit4: ''
  });
  const [stickyActionTexts, setStickyActionTexts] = useState<StickyActionTexts>({
    hero: '',
    highlights: '',
    gallery: '',
    overview: '',
    paymentPlan: '',
    location: '',
    leadForm: '',
    faq: ''
  });
  const [sectionSocialProof, setSectionSocialProof] = useState<SectionSocialProof>({
    hero: '',
    highlights: '',
    gallery: '',
    overview: '',
    paymentPlan: '',
    location: '',
    leadForm: '',
    faq: ''
  });
  const [showMenuOnMobile, setShowMenuOnMobile] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Fetch current settings
  const currentSettings = useQuery(api.landingPageSettings.getLandingPageSettings, {
    projectId,
    languageCode,
  });

  // Mutation to save settings
  const saveSettings = useMutation(api.landingPageSettings.upsertLandingPageSettings);

  // Load settings when data is available
  useEffect(() => {
    if (currentSettings) {
      setMenuItems(currentSettings.navigationSettings.menuItems);
      setCtaButtons(currentSettings.navigationSettings.ctaButtons);
      setBrandingSettings(currentSettings.brandingSettings);
      setContactSettings(currentSettings.contactSettings);
      setContentLabels(currentSettings.contentLabels);
      setShowMenuOnMobile(currentSettings.navigationSettings.showMenuOnMobile);
      
      // Load sticky action texts with fallback defaults
      if (currentSettings.navigationSettings.stickyActionTexts) {
        setStickyActionTexts(currentSettings.navigationSettings.stickyActionTexts);
      }
      if (currentSettings.navigationSettings.sectionSocialProof) {
        setSectionSocialProof(currentSettings.navigationSettings.sectionSocialProof);
      }
    }
  }, [currentSettings]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus('idle');

    try {
      await saveSettings({
        projectId,
        languageCode,
        navigationSettings: {
          menuItems,
          ctaButtons,
          showMenuOnMobile,
          stickyActionTexts,
          sectionSocialProof,
        },
        brandingSettings,
        contactSettings,
        contentLabels,
      });

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error saving navigation settings:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMenuItemChange = (index: number, field: keyof NavigationMenuItem, value: string | boolean | number) => {
    const updatedItems = [...menuItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setMenuItems(updatedItems);
  };

  const moveMenuItem = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === menuItems.length - 1)
    ) {
      return;
    }

    const newItems = [...menuItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Swap items
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    
    // Update order values
    newItems.forEach((item, idx) => {
      item.order = idx + 1;
    });

    setMenuItems(newItems);
  };

  const handleContentLabelChange = (field: keyof ContentLabels, value: string) => {
    setContentLabels({ ...contentLabels, [field]: value });
  };

  if (!currentSettings) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">
          Navigation Settings
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Manage navigation menu, branding, and contact settings for {languageCode.toUpperCase()}
        </p>
      </div>

      <div className="p-6 space-y-8">
        {/* Menu Items */}
        <div>
          <h4 className="text-base font-medium text-gray-900 mb-4">Menu Items</h4>
          <div className="space-y-3">
            {menuItems.map((item, index) => (
              <div key={item.id} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg">
                <div className="flex-1 grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Label
                    </label>
                    <input
                      type="text"
                      value={item.label}
                      onChange={(e) => handleMenuItemChange(index, 'label', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Section ID
                    </label>
                    <input
                      type="text"
                      value={item.id}
                      disabled
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={item.isEnabled}
                        onChange={(e) => handleMenuItemChange(index, 'isEnabled', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Enabled</span>
                    </label>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => moveMenuItem(index, 'up')}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => moveMenuItem(index, 'down')}
                    disabled={index === menuItems.length - 1}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    ↓
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Buttons */}
        <div>
          <h4 className="text-base font-medium text-gray-900 mb-4">CTA Buttons</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary Button Text
              </label>
              <input
                type="text"
                value={ctaButtons.primary}
                onChange={(e) => setCtaButtons({ ...ctaButtons, primary: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Download Brochure"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secondary Button Text
              </label>
              <input
                type="text"
                value={ctaButtons.secondary}
                onChange={(e) => setCtaButtons({ ...ctaButtons, secondary: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Contact Expert"
              />
            </div>
          </div>
        </div>

        {/* Sticky Action Texts */}
        <div>
          <h4 className="text-base font-medium text-gray-900 mb-4">Sticky Action Texts</h4>
          <p className="text-sm text-gray-600 mb-4">
            Customize the call-to-action text that appears in the sticky action button for each section.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hero Section
              </label>
              <input
                type="text"
                value={stickyActionTexts.hero}
                onChange={(e) => setStickyActionTexts({ ...stickyActionTexts, hero: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Get Latest Price & Availability"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Highlights Section
              </label>
              <input
                type="text"
                value={stickyActionTexts.highlights}
                onChange={(e) => setStickyActionTexts({ ...stickyActionTexts, highlights: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Book Private Meeting"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gallery Section
              </label>
              <input
                type="text"
                value={stickyActionTexts.gallery}
                onChange={(e) => setStickyActionTexts({ ...stickyActionTexts, gallery: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Book Consultation Now"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Overview Section
              </label>
              <input
                type="text"
                value={stickyActionTexts.overview}
                onChange={(e) => setStickyActionTexts({ ...stickyActionTexts, overview: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Get Latest Price & Availability"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Plan Section
              </label>
              <input
                type="text"
                value={stickyActionTexts.paymentPlan}
                onChange={(e) => setStickyActionTexts({ ...stickyActionTexts, paymentPlan: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Book Private Meeting"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Section
              </label>
              <input
                type="text"
                value={stickyActionTexts.location}
                onChange={(e) => setStickyActionTexts({ ...stickyActionTexts, location: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Book Consultation Now"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lead Form Section
              </label>
              <input
                type="text"
                value={stickyActionTexts.leadForm}
                onChange={(e) => setStickyActionTexts({ ...stickyActionTexts, leadForm: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Book Consultation Now"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                FAQ Section
              </label>
              <input
                type="text"
                value={stickyActionTexts.faq}
                onChange={(e) => setStickyActionTexts({ ...stickyActionTexts, faq: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Fast Response Guaranteed"
              />
            </div>
          </div>
        </div>

        {/* Section Social Proof */}
        <div>
          <h4 className="text-base font-medium text-gray-900 mb-4">Section Social Proof Messages</h4>
          <p className="text-sm text-gray-600 mb-4">
            Customize the social proof text that appears below the sticky action button for each section.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hero Section
              </label>
              <input
                type="text"
                value={sectionSocialProof.hero}
                onChange={(e) => setSectionSocialProof({ ...sectionSocialProof, hero: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Priority Selection Open"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Highlights Section
              </label>
              <input
                type="text"
                value={sectionSocialProof.highlights}
                onChange={(e) => setSectionSocialProof({ ...sectionSocialProof, highlights: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Get Floor Plan & Project Details"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gallery Section
              </label>
              <input
                type="text"
                value={sectionSocialProof.gallery}
                onChange={(e) => setSectionSocialProof({ ...sectionSocialProof, gallery: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Priority Selection Open"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Overview Section
              </label>
              <input
                type="text"
                value={sectionSocialProof.overview}
                onChange={(e) => setSectionSocialProof({ ...sectionSocialProof, overview: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Get Floor Plan & Project Details"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Plan Section
              </label>
              <input
                type="text"
                value={sectionSocialProof.paymentPlan}
                onChange={(e) => setSectionSocialProof({ ...sectionSocialProof, paymentPlan: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Priority Selection Open"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Section
              </label>
              <input
                type="text"
                value={sectionSocialProof.location}
                onChange={(e) => setSectionSocialProof({ ...sectionSocialProof, location: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Get Floor Plan & Project Details"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lead Form Section
              </label>
              <input
                type="text"
                value={sectionSocialProof.leadForm}
                onChange={(e) => setSectionSocialProof({ ...sectionSocialProof, leadForm: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Fast Response Guaranteed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                FAQ Section
              </label>
              <input
                type="text"
                value={sectionSocialProof.faq}
                onChange={(e) => setSectionSocialProof({ ...sectionSocialProof, faq: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Expert Consultation Available"
              />
            </div>
          </div>
        </div>

        {/* Branding Settings */}
        <div>
          <h4 className="text-base font-medium text-gray-900 mb-4">Branding</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Logo Text
              </label>
              <input
                type="text"
                value={brandingSettings.logoText}
                onChange={(e) => setBrandingSettings({ ...brandingSettings, logoText: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Company Name"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={brandingSettings.showLogo}
                  onChange={(e) => setBrandingSettings({ ...brandingSettings, showLogo: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Show Logo</span>
              </label>
            </div>
          </div>
        </div>

        {/* Contact Settings */}
        <div>
          <h4 className="text-base font-medium text-gray-900 mb-4">Contact Information</h4>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp Number
              </label>
              <input
                type="text"
                value={contactSettings.whatsappNumber}
                onChange={(e) => setContactSettings({ ...contactSettings, whatsappNumber: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="+971585999333"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp Default Message
              </label>
              <textarea
                value={contactSettings.whatsappMessage}
                onChange={(e) => setContactSettings({ ...contactSettings, whatsappMessage: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                rows={3}
                placeholder="Hi, I am interested in this property"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number (Optional)
              </label>
              <input
                type="text"
                value={contactSettings.phoneNumber || ''}
                onChange={(e) => setContactSettings({ ...contactSettings, phoneNumber: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="+971585999333"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={contactSettings.displayPhoneNumber}
                  onChange={(e) => setContactSettings({ ...contactSettings, displayPhoneNumber: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Display Phone Number</span>
              </label>
            </div>
          </div>
        </div>

        {/* Content Labels */}
        <div>
          <h4 className="text-base font-medium text-gray-900 mb-4">Content Labels</h4>
          <div className="grid grid-cols-1 gap-4">
            
            {/* Basic Labels */}
            <div className="border-b pb-4 mb-4">
              <h5 className="text-sm font-semibold text-gray-800 mb-3">Basic Labels</h5>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Available Unit Types
                  </label>
                  <input
                    type="text"
                    value={contentLabels.availableUnitTypes}
                    onChange={(e) => handleContentLabelChange('availableUnitTypes', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit Size
                  </label>
                  <input
                    type="text"
                    value={contentLabels.unitSize}
                    onChange={(e) => handleContentLabelChange('unitSize', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Starting From
                  </label>
                  <input
                    type="text"
                    value={contentLabels.startingFrom}
                    onChange={(e) => handleContentLabelChange('startingFrom', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Download Brochure
                  </label>
                  <input
                    type="text"
                    value={contentLabels.downloadBrochure}
                    onChange={(e) => handleContentLabelChange('downloadBrochure', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Expert
                  </label>
                  <input
                    type="text"
                    value={contentLabels.contactExpert}
                    onChange={(e) => handleContentLabelChange('contactExpert', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    View Details
                  </label>
                  <input
                    type="text"
                    value={contentLabels.viewDetails}
                    onChange={(e) => handleContentLabelChange('viewDetails', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Request Info
                  </label>
                  <input
                    type="text"
                    value={contentLabels.requestInfo}
                    onChange={(e) => handleContentLabelChange('requestInfo', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Book Tour
                  </label>
                  <input
                    type="text"
                    value={contentLabels.bookTour}
                    onChange={(e) => handleContentLabelChange('bookTour', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WhatsApp Action
                  </label>
                  <input
                    type="text"
                    value={contentLabels.whatsappAction}
                    onChange={(e) => handleContentLabelChange('whatsappAction', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Call Action
                  </label>
                  <input
                    type="text"
                    value={contentLabels.callAction}
                    onChange={(e) => handleContentLabelChange('callAction', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Learn More
                  </label>
                  <input
                    type="text"
                    value={contentLabels.learnMore}
                    onChange={(e) => handleContentLabelChange('learnMore', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    View All
                  </label>
                  <input
                    type="text"
                    value={contentLabels.viewAll}
                    onChange={(e) => handleContentLabelChange('viewAll', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Show More
                  </label>
                  <input
                    type="text"
                    value={contentLabels.showMore}
                    onChange={(e) => handleContentLabelChange('showMore', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Show Less
                  </label>
                  <input
                    type="text"
                    value={contentLabels.showLess}
                    onChange={(e) => handleContentLabelChange('showLess', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Payment Plan Labels */}
            <div className="border-b pb-4 mb-4">
              <h5 className="text-sm font-semibold text-gray-800 mb-3">Payment Plan Labels</h5>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Plan
                  </label>
                  <input
                    type="text"
                    value={contentLabels.paymentPlan}
                    onChange={(e) => handleContentLabelChange('paymentPlan', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Down Payment
                  </label>
                  <input
                    type="text"
                    value={contentLabels.downPayment}
                    onChange={(e) => handleContentLabelChange('downPayment', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due By
                  </label>
                  <input
                    type="text"
                    value={contentLabels.dueBy}
                    onChange={(e) => handleContentLabelChange('dueBy', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    No Payment Plans Available
                  </label>
                  <input
                    type="text"
                    value={contentLabels.noPaymentPlans}
                    onChange={(e) => handleContentLabelChange('noPaymentPlans', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* FAQ Labels */}
            <div className="border-b pb-4 mb-4">
              <h5 className="text-sm font-semibold text-gray-800 mb-3">FAQ Labels</h5>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frequently Asked Questions
                  </label>
                  <input
                    type="text"
                    value={contentLabels.frequentlyAskedQuestions}
                    onChange={(e) => handleContentLabelChange('frequentlyAskedQuestions', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    FAQ Description
                  </label>
                  <input
                    type="text"
                    value={contentLabels.faqDescription}
                    onChange={(e) => handleContentLabelChange('faqDescription', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Location Labels */}
            <div className="border-b pb-4 mb-4">
              <h5 className="text-sm font-semibold text-gray-800 mb-3">Location Labels</h5>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prime Location
                  </label>
                  <input
                    type="text"
                    value={contentLabels.primeLocation}
                    onChange={(e) => handleContentLabelChange('primeLocation', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nearby Places
                  </label>
                  <input
                    type="text"
                    value={contentLabels.nearbyPlaces}
                    onChange={(e) => handleContentLabelChange('nearbyPlaces', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Key Distances
                  </label>
                  <input
                    type="text"
                    value={contentLabels.keyDistances}
                    onChange={(e) => handleContentLabelChange('keyDistances', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    No Nearby Places
                  </label>
                  <input
                    type="text"
                    value={contentLabels.noNearbyPlaces}
                    onChange={(e) => handleContentLabelChange('noNearbyPlaces', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    No Key Distances
                  </label>
                  <input
                    type="text"
                    value={contentLabels.noKeyDistances}
                    onChange={(e) => handleContentLabelChange('noKeyDistances', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Gallery Labels */}
            <div className="border-b pb-4 mb-4">
              <h5 className="text-sm font-semibold text-gray-800 mb-3">Gallery Labels</h5>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Gallery
                  </label>
                  <input
                    type="text"
                    value={contentLabels.projectGallery}
                    onChange={(e) => handleContentLabelChange('projectGallery', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Close Gallery
                  </label>
                  <input
                    type="text"
                    value={contentLabels.closeGallery}
                    onChange={(e) => handleContentLabelChange('closeGallery', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Legal Info Labels */}
            <div className="border-b pb-4 mb-4">
              <h5 className="text-sm font-semibold text-gray-800 mb-3">Legal Info Labels</h5>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DLD Permit Number
                  </label>
                  <input
                    type="text"
                    value={contentLabels.dldPermitNumber}
                    onChange={(e) => handleContentLabelChange('dldPermitNumber', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scan QR Code
                  </label>
                  <input
                    type="text"
                    value={contentLabels.scanQrCode}
                    onChange={(e) => handleContentLabelChange('scanQrCode', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Form Labels */}
            <div className="border-b pb-4 mb-4">
              <h5 className="text-sm font-semibold text-gray-800 mb-3">Form Labels</h5>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={contentLabels.fullName}
                    onChange={(e) => handleContentLabelChange('fullName', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={contentLabels.firstName}
                    onChange={(e) => handleContentLabelChange('firstName', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={contentLabels.lastName}
                    onChange={(e) => handleContentLabelChange('lastName', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="text"
                    value={contentLabels.email}
                    onChange={(e) => handleContentLabelChange('email', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    value={contentLabels.phoneNumber}
                    onChange={(e) => handleContentLabelChange('phoneNumber', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Continue
                  </label>
                  <input
                    type="text"
                    value={contentLabels.continue}
                    onChange={(e) => handleContentLabelChange('continue', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Back
                  </label>
                  <input
                    type="text"
                    value={contentLabels.back}
                    onChange={(e) => handleContentLabelChange('back', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Submit
                  </label>
                  <input
                    type="text"
                    value={contentLabels.submit}
                    onChange={(e) => handleContentLabelChange('submit', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Submitting
                  </label>
                  <input
                    type="text"
                    value={contentLabels.submitting}
                    onChange={(e) => handleContentLabelChange('submitting', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Thank You
                  </label>
                  <input
                    type="text"
                    value={contentLabels.thankYou}
                    onChange={(e) => handleContentLabelChange('thankYou', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search Countries
                  </label>
                  <input
                    type="text"
                    value={contentLabels.searchCountries}
                    onChange={(e) => handleContentLabelChange('searchCountries', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Lead Form Content */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium border-b pb-2">Lead Form Content</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="getPersonalizedDetails" className="block text-sm font-medium text-gray-700 mb-1">Get Personalized Details</label>
                  <input
                    id="getPersonalizedDetails"
                    value={contentLabels.getPersonalizedDetails || ''}
                    onChange={(e) => handleContentLabelChange('getPersonalizedDetails', e.target.value)}
                    placeholder="Get Personalized Property Details"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="speakWithExperts" className="block text-sm font-medium text-gray-700 mb-1">Speak with Experts</label>
                  <input
                    id="speakWithExperts"
                    value={contentLabels.speakWithExperts || ''}
                    onChange={(e) => handleContentLabelChange('speakWithExperts', e.target.value)}
                    placeholder="Speak with our property experts and get:"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="getStartedNow" className="block text-sm font-medium text-gray-700 mb-1">Get Started Now</label>
                  <input
                    id="getStartedNow"
                    value={contentLabels.getStartedNow || ''}
                    onChange={(e) => handleContentLabelChange('getStartedNow', e.target.value)}
                    placeholder="Get Started Now"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="takesOnlySeconds" className="block text-sm font-medium text-gray-700 mb-1">Takes Only Seconds</label>
                  <input
                    id="takesOnlySeconds"
                    value={contentLabels.takesOnlySeconds || ''}
                    onChange={(e) => handleContentLabelChange('takesOnlySeconds', e.target.value)}
                    placeholder="Takes only a few seconds"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Lead Form Benefits */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium border-b pb-2">Lead Form Benefits</h3>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <label htmlFor="benefit1" className="block text-sm font-medium text-gray-700 mb-1">Benefit 1</label>
                  <input
                    id="benefit1"
                    value={contentLabels.benefit1 || ''}
                    onChange={(e) => handleContentLabelChange('benefit1', e.target.value)}
                    placeholder="Detailed floor plans & pricing"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="benefit2" className="block text-sm font-medium text-gray-700 mb-1">Benefit 2</label>
                  <input
                    id="benefit2"
                    value={contentLabels.benefit2 || ''}
                    onChange={(e) => handleContentLabelChange('benefit2', e.target.value)}
                    placeholder="Virtual tour of the property"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="benefit3" className="block text-sm font-medium text-gray-700 mb-1">Benefit 3</label>
                  <input
                    id="benefit3"
                    value={contentLabels.benefit3 || ''}
                    onChange={(e) => handleContentLabelChange('benefit3', e.target.value)}
                    placeholder="Payment plan options"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="benefit4" className="block text-sm font-medium text-gray-700 mb-1">Benefit 4</label>
                  <input
                    id="benefit4"
                    value={contentLabels.benefit4 || ''}
                    onChange={(e) => handleContentLabelChange('benefit4', e.target.value)}
                    placeholder="Investment analysis report"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Settings */}
        <div>
          <h4 className="text-base font-medium text-gray-900 mb-4">Mobile Settings</h4>
          <div className="flex items-center">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showMenuOnMobile}
                onChange={(e) => setShowMenuOnMobile(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Show Menu on Mobile</span>
            </label>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
        <div>
          {saveStatus === 'success' && (
            <span className="text-sm text-green-600">Settings saved successfully!</span>
          )}
          {saveStatus === 'error' && (
            <span className="text-sm text-red-600">Error saving settings. Please try again.</span>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
} 